import {
  useIsCommunityAdmin,
  usePunditUserContext,
} from "@circle-react/contexts/punditUserContext";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useLayoutEffect, useRef } from "react";

import { useAvatarConversationPanelStore } from "../useAvatarConversationPanelStore";
import { useIsAvatarsV2Layout } from "../useIsAvatarsV2Layout";
import {
  AVATAR_CHAT_ID_PARAM,
  AVATAR_ID_PARAM,
  AVATAR_PARAM,
  AVATAR_VIEW_TYPE_PARAM,
  type AvatarPanelSearchParams,
  parseAvatarPanelUrlEntry,
} from "./urlParams";
import { useOpenSharedAvatar } from "./useOpenSharedAvatar";

export const useAvatarPanelUrlEntry = (): void => {
  const isAvatarsV2Layout = useIsAvatarsV2Layout();
  const isAdmin = useIsCommunityAdmin();
  const { isLoading: isPunditLoading } = usePunditUserContext();
  const search: AvatarPanelSearchParams = useSearch({ strict: false });
  const navigate = useNavigate();
  const openSharedAvatar = useOpenSharedAvatar();

  const entry = parseAvatarPanelUrlEntry(search);
  const hasEntry = entry !== null;
  const chatId = entry?.chatId ?? null;
  const avatarId = entry?.avatarId ?? null;
  const view = entry?.view ?? "default";
  const hasConsumedRef = useRef(false);

  // useLayoutEffect, not useEffect: a deep link must not flash closed -> open.
  useLayoutEffect(() => {
    // Params gone: re-arm so a later in-session deep link is picked up.
    if (!hasEntry) {
      hasConsumedRef.current = false;
      return;
    }
    if (hasConsumedRef.current) return;
    // `isAdmin` reads false while the payload is in flight.
    if (isPunditLoading) return;

    hasConsumedRef.current = true;

    if (isAvatarsV2Layout && !isAdmin) {
      if (chatId === null && avatarId !== null) {
        void openSharedAvatar(avatarId, view);
      } else {
        useAvatarConversationPanelStore
          .getState()
          .openPanel({ chatId, avatarId, panelState: view });
      }
    }

    // `replace` is load-bearing: the triggering URL would otherwise sit in
    // history and re-fire this effect on every back press.
    void navigate({
      to: ".",
      search: prev => ({
        ...prev,
        [AVATAR_PARAM]: undefined,
        [AVATAR_ID_PARAM]: undefined,
        [AVATAR_CHAT_ID_PARAM]: undefined,
        [AVATAR_VIEW_TYPE_PARAM]: undefined,
      }),
      replace: true,
      resetScroll: false,
    });
  }, [
    avatarId,
    chatId,
    hasEntry,
    isAdmin,
    isAvatarsV2Layout,
    isPunditLoading,
    navigate,
    openSharedAvatar,
    view,
  ]);
};
